import { fetchByIds } from "@/lib/large-org";
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { ShieldAlert, MapPin, Check, X, Send } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { SosButton } from "@/components/SosButton";
import { toast } from "sonner";
import { format } from "date-fns";
import {
  acknowledgeSos,
  resolveSos,
  cancelSos,
  postSosNote,
  toggleSosEmergencyServices,
} from "@/lib/sos.functions";

export const Route = createFileRoute("/_authenticated/sos")({
  component: SosPage,
  head: () => ({ meta: [{ title: "SOS — MissionReady" }] }),
});

type Alert = {
  id: string;
  organization_id: string;
  activator_id: string;
  site_id: string | null;
  severity: "staff" | "emergency";
  status: "active" | "acknowledged" | "resolved" | "cancelled";
  trigger_method: string;
  latitude: number | null;
  longitude: number | null;
  message: string | null;
  emergency_services_notified: boolean;
  acknowledged_by: string | null;
  acknowledged_at: string | null;
  resolved_by: string | null;
  resolved_at: string | null;
  resolution_note: string | null;
  created_at: string;
};

type Note = {
  id: number;
  alert_id: string;
  author_id: string;
  event_type: string;
  body: string | null;
  created_at: string;
};

type Profile = { id: string; full_name: string | null; email: string | null };

function SosPage() {
  const { currentOrg, user, currentRole } = useAuth();
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [notes, setNotes] = useState<Record<string, Note[]>>({});
  const [profiles, setProfiles] = useState<Record<string, Profile>>({});
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [resolveNote, setResolveNote] = useState<Record<string, string>>({});

  const canRespond = ["org_owner", "admin", "supervisor", "dispatcher"].includes(currentRole ?? "");

  const acknowledgeSosFn = useServerFn(acknowledgeSos);
  const resolveSosFn = useServerFn(resolveSos);
  const cancelSosFn = useServerFn(cancelSos);
  const postSosNoteFn = useServerFn(postSosNote);
  const toggleSosEmergencyServicesFn = useServerFn(toggleSosEmergencyServices);
  useEffect(() => {
    if (!currentOrg) return;
    const orgId = currentOrg.id;
    const load = async () => {
      const { data: a } = await supabase
        .from("sos_alerts")
        .select("*")
        .eq("organization_id", orgId)
        .order("created_at", { ascending: false })
        .limit(100);
      const list = (a ?? []) as Alert[];
      setAlerts(list);

      const ids = list.map((x) => x.id);
      if (ids.length) {
        const { data: n } = await supabase
          .from("sos_responder_notes")
          .select("*")
          .in("alert_id", ids)
          .order("created_at", { ascending: true });
        const grouped: Record<string, Note[]> = {};
        ((n ?? []) as Note[]).forEach((row) => {
          (grouped[row.alert_id] ||= []).push(row);
        });
        setNotes(grouped);
      }

      const userIds = Array.from(
        new Set(list.flatMap((x) => [x.activator_id, x.acknowledged_by, x.resolved_by]).filter(Boolean) as string[]),
      );
      if (userIds.length) {
        const { data: p } = await fetchByIds<any>("profiles", "id, full_name, email", userIds, "id", supabase).then((d) => ({ data: d, error: null }));
        const map: Record<string, Profile> = {};
        ((p ?? []) as Profile[]).forEach((pr) => { map[pr.id] = pr; });
        setProfiles(map);
      }
    };
    load();
    const ch = supabase
      .channel(`sos-page:${orgId}:${Math.random().toString(36).slice(2, 10)}`)
      .on("postgres_changes", { event: "*", schema: "public", table: "sos_alerts", filter: `organization_id=eq.${orgId}` }, load)
      .on("postgres_changes", { event: "*", schema: "public", table: "sos_responder_notes", filter: `organization_id=eq.${orgId}` }, load)
      .subscribe();
    return () => { supabase.removeChannel(ch); };
  }, [currentOrg]);

  const active = useMemo(() => alerts.filter((a) => a.status === "active" || a.status === "acknowledged"), [alerts]);
  const past = useMemo(() => alerts.filter((a) => a.status === "resolved" || a.status === "cancelled"), [alerts]);

  const nameOf = (id: string | null) => {
    if (!id) return "—";
    const p = profiles[id];
    return p?.full_name || p?.email || id.slice(0, 8);
  };

  const acknowledge = async (a: Alert) => {
    if (!user) return;
    try {
      await acknowledgeSosFn({ data: { alertId: a.id } });
      toast.success("Acknowledged");
    } catch (e: any) { toast.error(e?.message ?? "Failed"); }
  };

  const resolve = async (a: Alert) => {
    if (!user) return;
    const note = (resolveNote[a.id] ?? "").trim();
    try {
      await resolveSosFn({ data: { alertId: a.id, note: note || undefined } });
      setResolveNote((s) => ({ ...s, [a.id]: "" }));
      toast.success("Resolved");
    } catch (e: any) { toast.error(e?.message ?? "Failed"); }
  };

  const cancel = async (a: Alert) => {
    if (!user) return;
    try {
      await cancelSosFn({ data: { alertId: a.id } });
      toast.success("SOS cancelled");
    } catch (e: any) { toast.error(e?.message ?? "Failed"); }
  };

  const postNote = async (a: Alert) => {
    if (!user) return;
    const body = (draft[a.id] ?? "").trim();
    if (!body) return;
    try {
      await postSosNoteFn({ data: { alertId: a.id, body } });
      setDraft((s) => ({ ...s, [a.id]: "" }));
    } catch (e: any) { toast.error(e?.message ?? "Failed"); }
  };

  const toggleEmergencyServices = async (a: Alert, on: boolean) => {
    if (!user) return;
    try {
      await toggleSosEmergencyServicesFn({ data: { alertId: a.id, on } });
    } catch (e: any) { toast.error(e?.message ?? "Failed"); }
  };
  return (
    <div className="px-4 py-6 md:px-8 md:py-8">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-2">
          <ShieldAlert className="h-5 w-5 text-destructive" />
          <h1 className="font-display text-2xl font-bold md:text-3xl">SOS / Panic</h1>
        </div>
        <SosButton />
      </div>
      <p className="text-sm text-muted-foreground">
        Hold the red button for 3 seconds to send an SOS. Double-tap then hold for 5 seconds for an EMERGENCY alert.
      </p>

      <section className="mt-6">
        <h2 className="font-display text-lg font-semibold">Active alerts</h2>
        {active.length === 0 ? (
          <div className="mt-3 rounded-xl border border-border bg-card p-6 text-center text-sm text-muted-foreground">
            No active SOS alerts.
          </div>
        ) : (
          <div className="mt-3 space-y-3">
            {active.map((a) => {
              const isEmergency = a.severity === "emergency";
              const aNotes = notes[a.id] ?? [];
              return (
                <article
                  key={a.id}
                  className={`overflow-hidden rounded-xl border ${
                    isEmergency ? "border-destructive bg-destructive/5" : "border-warning/40 bg-warning/5"
                  }`}
                >
                  <header className={`flex flex-wrap items-center gap-2 px-4 py-3 ${isEmergency ? "bg-destructive/10" : "bg-warning/10"}`}>
                    <span className={`rounded-full px-2 py-0.5 text-xs font-bold uppercase text-white ${isEmergency ? "bg-destructive" : "bg-warning"}`}>
                      {a.severity}
                    </span>
                    <span className="text-xs uppercase tracking-wide text-muted-foreground">{a.status}</span>
                    <span className="text-sm font-medium">{nameOf(a.activator_id)}</span>
                    <span className="text-xs text-muted-foreground">{format(new Date(a.created_at), "MMM d, HH:mm:ss")}</span>
                    {a.latitude != null && a.longitude != null && (
                      <a
                        href={`https://www.google.com/maps?q=${a.latitude},${a.longitude}`}
                        target="_blank" rel="noreferrer"
                        className="ml-auto inline-flex items-center gap-1 text-xs text-primary hover:underline"
                      >
                        <MapPin className="h-3 w-3" />
                        {a.latitude.toFixed(5)}, {a.longitude.toFixed(5)}
                      </a>
                    )}
                  </header>

                  <div className="space-y-3 p-4">
                    <div className="flex flex-wrap gap-2">
                      {canRespond && a.status === "active" && (
                        <Button size="sm" onClick={() => acknowledge(a)}><Check className="mr-1 h-4 w-4" /> Acknowledge</Button>
                      )}
                      {a.activator_id === user?.id && (
                        <Button size="sm" variant="outline" onClick={() => cancel(a)}><X className="mr-1 h-4 w-4" /> Cancel my alert</Button>
                      )}
                      {canRespond && (
                        <div className="flex items-center gap-2 rounded-md border border-border bg-background/60 px-3 py-1.5">
                          <Switch
                            id={`em-${a.id}`}
                            checked={a.emergency_services_notified}
                            onCheckedChange={(v) => toggleEmergencyServices(a, v)}
                          />
                          <Label htmlFor={`em-${a.id}`} className="text-xs">Emergency services notified</Label>
                        </div>
                      )}
                    </div>

                    {/* Timeline */}
                    <div className="space-y-1.5 rounded-md border border-border bg-background/40 p-3">
                      {aNotes.length === 0 && <p className="text-xs text-muted-foreground">No responder notes yet.</p>}
                      {aNotes.map((n) => (
                        <div key={n.id} className="flex gap-2 text-xs">
                          <span className="text-muted-foreground">{format(new Date(n.created_at), "HH:mm:ss")}</span>
                          <span className="font-medium">{nameOf(n.author_id)}</span>
                          <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] uppercase">{n.event_type}</span>
                          <span className="flex-1">{n.body}</span>
                        </div>
                      ))}
                    </div>

                    {canRespond && (
                      <div className="space-y-2">
                        <div className="flex gap-2">
                          <Textarea
                            value={draft[a.id] ?? ""}
                            onChange={(e) => setDraft((s) => ({ ...s, [a.id]: e.target.value }))}
                            placeholder="Add responder note (en-route, on-scene, status, etc.)"
                            rows={2}
                            className="text-sm"
                          />
                          <Button size="sm" onClick={() => postNote(a)}><Send className="h-4 w-4" /></Button>
                        </div>
                        <div className="flex gap-2">
                          <Textarea
                            value={resolveNote[a.id] ?? ""}
                            onChange={(e) => setResolveNote((s) => ({ ...s, [a.id]: e.target.value }))}
                            placeholder="Resolution note (optional)"
                            rows={1}
                            className="text-sm"
                          />
                          <Button size="sm" variant="default" onClick={() => resolve(a)}>Resolve</Button>
                        </div>
                      </div>
                    )}
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </section>

      <section className="mt-8">
        <h2 className="font-display text-lg font-semibold">Recent</h2>
        <div className="mt-3 overflow-hidden rounded-xl border border-border bg-card">
          <div className="overflow-x-auto">
          <table className="w-full min-w-[34rem] text-sm">

            <thead className="bg-muted/40 text-xs uppercase text-muted-foreground">
              <tr>
                <th className="px-4 py-2 text-left">When</th>
                <th className="px-4 py-2 text-left">Activator</th>
                <th className="px-4 py-2 text-left">Severity</th>
                <th className="px-4 py-2 text-left">Status</th>
                <th className="px-4 py-2 text-left">Resolved by</th>
                <th className="px-4 py-2 text-left">Note</th>
              </tr>
            </thead>
            <tbody>
              {past.length === 0 && <tr><td colSpan={6} className="px-4 py-6 text-center text-muted-foreground">No past alerts.</td></tr>}
              {past.map((a) => (
                <tr key={a.id} className="border-t border-border">
                  <td className="px-4 py-2">{format(new Date(a.created_at), "MMM d, HH:mm")}</td>
                  <td className="px-4 py-2">{nameOf(a.activator_id)}</td>
                  <td className="px-4 py-2 capitalize">{a.severity}</td>
                  <td className="px-4 py-2 capitalize">{a.status}</td>
                  <td className="px-4 py-2">{nameOf(a.resolved_by)}</td>
                  <td className="px-4 py-2 truncate">{a.resolution_note ?? "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>

      </section>
    </div>
  );
}
